<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { emitter } from 'shared/helpers/mitt';
import { useSidepanelToggle } from 'dashboard/composables/useSidepanelToggle';

import InboxItemHeader from './components/InboxItemHeader.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import InboxEmptyState from './InboxEmptyState.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings } = useUISettings();
useSidepanelToggle();

const isConversationLoading = ref(false);
const conversationLoadError = ref(false);

const notification = useMapGetter('notifications/getFilteredNotifications');
const currentChat = useMapGetter('getSelectedChat');
const conversationById = useMapGetter('getConversationById');
const meta = useMapGetter('notifications/getMeta');

const inboxId = computed(() => Number(route.params.inboxId));
const conversationId = computed(() => Number(route.params.id));

const activeSortOrder = computed(() => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const { sort_by: sortBy } = filterBy;
  return sortBy || 'desc';
});

const notifications = computed(() => {
  return notification.value({
    sortOrder: activeSortOrder.value,
  });
});

const activeNotification = computed(() => {
  return notifications.value?.find(
    n => n.primary_actor?.id === conversationId.value
  );
});

const totalNotificationCount = computed(() => {
  return meta.value.count;
});

const showEmptyState = computed(() => {
  return !conversationId.value;
});

const activeNotificationIndex = computed(() => {
  return notifications.value?.findIndex(
    n => n.primary_actor?.id === conversationId.value
  );
});

const isContactPanelOpen = computed(() => {
  if (currentChat.value.id) {
    const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
    return isContactSidebarOpen;
  }
  return false;
});

// Once the Details panel has been opened it stays mounted and toggles with
// v-show, so switching tabs never remounts it (no flicker, no refetch).
const detailsPanelWasOpened = ref(false);
watch(
  isContactPanelOpen,
  isOpen => {
    if (isOpen) detailsPanelWasOpened.value = true;
  },
  { immediate: true }
);

const findConversation = () => {
  return conversationById.value(conversationId.value);
};

const openNotification = async notificationItem => {
  const {
    id,
    primary_actor_id: primaryActorId,
    primary_actor_type: primaryActorType,
    primary_actor: { id: conversationIdFromNotification },
    notification_type: notificationType,
  } = notificationItem;

  useTrack(INBOX_EVENTS.OPEN_CONVERSATION_VIA_INBOX, {
    notificationType,
  });

  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });

    // to update the unread count in the store realtime
    store.dispatch('notifications/unReadCount');
  } catch {
    useAlert(t('INBOX.ALERTS.MARK_AS_READ_ERROR'));
  }

  // Marking the notification as read is bookkeeping, opening the conversation
  // is the intent, so navigate even when the read dispatch fails.
  router.push({
    name: 'inbox_view_conversation',
    params: { type: 'conversation', id: conversationIdFromNotification },
  });
};

const setActiveChat = async () => {
  const selectedConversation = findConversation();
  if (!selectedConversation) return;

  try {
    await store.dispatch('setActiveChat', { data: selectedConversation });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
  } catch {
    // error
  }
};

const fetchConversationById = async () => {
  if (!conversationId.value) return;

  conversationLoadError.value = false;
  store.dispatch('clearSelectedState');
  const existingChat = findConversation();

  if (existingChat) {
    await setActiveChat();
    return;
  }

  isConversationLoading.value = true;

  try {
    await store.dispatch('getConversation', conversationId.value);
    if (!findConversation()) {
      throw new Error('Conversation could not be loaded');
    }
    await setActiveChat();
  } catch {
    conversationLoadError.value = true;
  } finally {
    isConversationLoading.value = false;
  }
};

const navigateToConversation = (activeIndex, direction) => {
  const isValidPrev = direction === 'prev' && activeIndex > 0;
  const isValidNext =
    direction === 'next' && activeIndex < totalNotificationCount.value - 1;

  if (!isValidPrev && !isValidNext) return;

  const updatedIndex = direction === 'prev' ? activeIndex - 1 : activeIndex + 1;
  const targetNotification = notifications.value[updatedIndex];

  if (targetNotification) {
    openNotification(targetNotification);
  }
};

const onClickNext = () => {
  navigateToConversation(activeNotificationIndex.value, 'next');
};

const onClickPrev = () => {
  navigateToConversation(activeNotificationIndex.value, 'prev');
};

watch(
  conversationId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchConversationById();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await store.dispatch('agents/get');
});
</script>

<template>
  <div class="h-full w-full min-w-0 flex-1 bg-n-surface-2">
    <div v-if="showEmptyState" class="flex w-full h-full">
      <InboxEmptyState
        :empty-state-message="$t('INBOX.WORKSPACE.EMPTY.SELECT_DETAIL')"
      />
    </div>
    <div v-else class="flex flex-col w-full h-full min-w-0">
      <InboxItemHeader
        :total-length="totalNotificationCount"
        :current-index="activeNotificationIndex"
        :active-notification="activeNotification"
        @next="onClickNext"
        @prev="onClickPrev"
      />
      <div
        v-if="conversationLoadError"
        role="alert"
        class="grid flex-1 p-6 place-items-center bg-n-surface-2"
      >
        <div
          class="flex flex-col items-center max-w-sm gap-3 p-6 text-center border shadow-sm rounded-2xl border-n-weak bg-n-solid-2"
        >
          <span
            class="grid rounded-full size-11 place-items-center bg-n-ruby-3 text-n-ruby-11"
          >
            <span class="i-lucide-message-square-warning size-5" />
          </span>
          <div>
            <h2 class="m-0 text-heading-2 text-n-slate-12">
              {{ $t('INBOX.WORKSPACE.ERROR.CONVERSATION_TITLE') }}
            </h2>
            <p class="mt-1 mb-0 text-sm text-n-slate-11">
              {{ $t('INBOX.WORKSPACE.ERROR.CONVERSATION_DETAIL') }}
            </p>
          </div>
          <Button
            :label="$t('INBOX.WORKSPACE.RETRY')"
            icon="i-lucide-refresh-cw"
            size="sm"
            @click="fetchConversationById"
          />
        </div>
      </div>
      <div
        v-else-if="isConversationLoading"
        role="status"
        aria-busy="true"
        :aria-label="$t('SKELETON.LOADING')"
        class="flex flex-col flex-1 gap-4 p-6 bg-n-surface-2"
      >
        <Skeleton :announce="false" class="w-2/3 h-16" />
        <Skeleton
          :announce="false"
          class="w-1/2 h-12 ltr:ml-auto rtl:mr-auto"
        />
        <Skeleton :announce="false" class="w-3/5 h-16" />
        <Skeleton :announce="false" class="w-full h-20 mt-auto" />
      </div>
      <div v-else class="flex flex-1 min-w-0 min-h-0">
        <ConversationBox
          class="flex-1 [&.conversation-details-wrap]:!border-0 [&_.conversation-panel]:bg-n-background [&_.reply-box]:!mx-3 [&_.reply-box]:!mb-3 [&_.reply-box]:!border-n-weak [&_.reply-box]:shadow-lg [&_.reply-box]:shadow-n-slate-12/5 [&_.conversation--header--actions]:text-n-slate-10"
          is-inbox-view
          :inbox-id="inboxId"
          :is-on-expanded-layout="false"
        />
        <ConversationSidebar
          v-if="currentChat.id && detailsPanelWasOpened"
          v-show="isContactPanelOpen"
          class="motion-safe:animate-panel-slide-in"
          :current-chat="currentChat"
        />
      </div>
    </div>
  </div>
</template>
